import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Link from 'next/link';
import Image from 'next/image';
import { LoadingSpinner } from '../../shared-components/LoadingSpinner/LoadingSpinner';
import { useMediaQuery } from '../../hooks/mediaQuery';
import { removeTripshepherdFromEnd } from '../../utils/trip-sheppered-text';

interface Category {
  id: string;
  name: string;
}

interface Blog {
  page_title: string;
  author: string;
  publication_date: string;
  image: string;
  slug: string;
  created_at: string;
}

const LatestPublications = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<string>('all-categories');
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const isNotDesktop = useMediaQuery(1280);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const blogsToDisplay = isNotDesktop
    ? showMore
      ? 9
      : 19
    : showMore
    ? 10
    : 19;

  const blogsMoreToDisplay = isNotDesktop ? 9 : 10;

  const handleToggle = () => {
    setShowMore(prev => !prev);
    if (!showMore) {
      const sectionElement = document.getElementById('focushere');
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const fetchAllBlogs = async () => {
    setLoading(true);

    setError(null);

    try {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blogs`,
        {
          params: {
            order: 'desc',
            limit: 21
          }
        }
      );

      let blogs = res.data.blogs || [];

      blogs = blogs.sort((a: any, b: any) => {
        return (
          new Date(b.publication_date).getTime() -
          new Date(a.publication_date).getTime()
        );
      });

      setBlogs(blogs);
    } catch (error) {
      setError('Failed to fetch blogs.');
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };
  const fetchBlogs = async (categoryID: string) => {
    if (!categoryID) return;
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(
        `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blogs`,
        {
          params: {
            order: 'desc',
            category: categoryID,
            limit: 21
          }
        }
      );

      let blogs = res.data.blogs || [];

      blogs = blogs.sort((a: any, b: any) => {
        return (
          new Date(b.publication_date).getTime() -
          new Date(a.publication_date).getTime()
        );
      });

      setBlogs(blogs);
    } catch (error) {
      setError('Failed to fetch blogs.');
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.NEXT_PUBLIC_FIREBASE_CONNECTION}/blog-page`
        );
        setCategories(response.data.blog_categories);
      } catch (error) {
        console.error('Error fetching categories', error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all-categories') {
      fetchAllBlogs();
    } else if (selectedCategory) {
      fetchBlogs(selectedCategory);
    }
  }, [selectedCategory]);

  return (
    <div className="w-full mt-[14rem] md:mt-[18rem]">
      <div className="flex flex-col  xmsm:flex-row justify-between mx-[6%] xl:mx-[12%]">
        <h2 className="text-[16px] font-bold text-primary order-1 xmsm:order-0 md:text-[32px] xl:text-[40px] mx-auto xmsm:mx-0">
          LATEST PUBLICATIONS
        </h2>
        <div className="relative w-[200px] xmsm:w-[240px] h-[45px]  cursor-pointer xmsm:order-1  md:w-[220px] md:h-[50px] xl:w-[240px] xl:h-[52px] mx-auto xmsm:mx-0 ">
          <div className="dropdown-wrapper relative" ref={dropdownRef}>
            <div
              className="w-full py-2 cursor-pointer flex items-center justify-between px-3 border border-3 border-black rounded-[1.2rem]"
              onClick={() => setShowDropdown(prev => !prev)}
            >
              <span className=" text-[14px] md:text-[16px] xl:text-[18px]">
                {selectedCategory === 'all-categories'
                  ? 'All Categories'
                  : categories.find(
                      category => category.id === selectedCategory
                    )?.name}
              </span>

              <span>
                <Image
                  src="/dropDown.png"
                  width={10}
                  height={10}
                  alt="dropdown"
                  className={`hover:cursor-pointer ${
                    showDropdown ? '' : 'rotate-180'
                  }`}
                />
              </span>
            </div>
            {showDropdown && (
              <div className="absolute w-full mt-1 bg-gray-100 border-2 border-gray-300 rounded-[1.2rem] z-10">
                <div
                  className="py-2 text-[12px] md:text-[14px] xl:text-[16px] cursor-pointer px-3 hover:bg-gray-100"
                  onClick={() => {
                    setSelectedCategory('all-categories');
                    setShowDropdown(false);
                  }}
                >
                  All Categories
                </div>
                {categories.map(category => (
                  <div
                    key={category.id}
                    className="py-2 text-[12px] md:text-[14px] xl:text-[16px] cursor-pointer px-3 hover:bg-gray-100"
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setShowDropdown(false);
                    }}
                  >
                    {category.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="w-full h-[500px] xl:h-[700px] flex justify-center items-center">
          <LoadingSpinner />
        </div>
      )}

      {error && <p className="text-red-500">{error}</p>}

      {!loading && !isInitialLoad && blogs.length === 0 && !error && (
        <div className="w-full h-[500px] pt-10 flex justify-center text-primary">
          No blogs available for this category
        </div>
      )}

      {!loading && !isInitialLoad && blogs.length > 0 && (
        <div className="mt-[1.5rem] w-full">
          <div className="flex flex-col gap-8 mx-[6%] xl:mx-[12%] mt-4 ">
            <Link href={`/blog/${blogs[0]?.slug}`}>
              <div className="flex flex-col bg-white w-full md:flex-row h-[320px] md:h-[250px] xl:h-[420px] md:mb-8 rounded-lg shadow-lg shadow-bottom">
                <div className="w-full md:w-[55%] h-[170px] md:h-full rounded-[1rem]">
                  <Image
                    src={blogs[0]?.image}
                    alt={blogs[0]?.page_title}
                    width={1920}
                    height={1080}
                    className="object-cover w-full h-full p-2 md:p-0 rounded-[1rem] md:rounded-lg md:rounded-r-none"
                  />
                </div>
                <div className="w-full h-2/3 md:h-full flex flex-col md:w-[45%] md:ml-4 relative pr-1 md:pt-4 ">
                  <div className="flex flex-row text-[#555555] text-left mt-2 text-[12px] md:text-[13px] xl:text-[16px] ml-4">
                    <Image
                      src={`/calendar-icon.svg`}
                      alt="calender-icon"
                      width={24}
                      height={24}
                      className="w-4 h-4 md:w-5 md:h-5"
                    />
                    <div className="pl-1">
                      {formatDate(blogs[0]?.publication_date)}
                    </div>
                  </div>
                  <div className="text-primary text-[16px] text-left font-bold mt-2 md:text-[23px] xl:text-[30px] ml-4 md:pt-0 flex-grow">
                    {removeTripshepherdFromEnd(blogs[0]?.page_title)}
                  </div>

                  <div className="text-[#808080] text-[14px] font-semibold mt-auto text-left md:text-[13px] xl:text-[16px] m-4">
                    {blogs[0]?.author}
                  </div>
                </div>
              </div>
            </Link>
            <div
              className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3 w-full px-0"
              id="focushere"
            >
              {blogs?.slice(1, blogsToDisplay).map((blog, index) => (
                <Link key={index} href={`/blog/${blog.slug}`}>
                  <div className="flex flex-col bg-white flex-shrink-0 h-[320px] xl:w-full xl:h-[370px] 3xl:h-[450px] md:h-[330px] lg:h-[350px] rounded-lg shadow-lg shadow-bottom relative">
                    <div className="w-full h-[170px] md:h-[180px] lg:h-[190px] xl:h-[200px] 2xl:h-[260px] rounded-[1rem]">
                      <Image
                        src={blog.image}
                        alt={blog.publication_date}
                        width={1920}
                        height={1080}
                        className="object-cover w-full h-full rounded-[1rem] p-2"
                      />
                    </div>
                    <div className="flex flex-row text-[#555555] text-left mt-2 text-[12px] md:text-[13px] xl:text-[16px] ml-4">
                      <Image
                        src={`/calendar-icon.svg`}
                        alt="calender-icon"
                        width={24}
                        height={24}
                        className="w-4 h-4 md:w-5 md:h-5"
                      />
                      <div className="pl-1">
                        {formatDate(blog.publication_date)}
                      </div>
                    </div>
                    <div className="text-primary text-[16px] text-left font-bold mt-2 md:text-[18px] xl:text-[20px] ml-4 pr-1">
                      {removeTripshepherdFromEnd(blog.page_title)}
                    </div>
                    <div className="text-[#808080] text-[14px] font-semibold mt-auto text-left md:text-[13px] xl:text-[16px] m-4">
                      {blog.author}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {blogs.length >= blogsMoreToDisplay && (
              <div className="flex justify-end mt-[1rem] ">
                <button
                  onClick={handleToggle}
                  className=" bg-primary text-white text-[18px] font-semibold w-[120px] rounded-lg cursor-pointer  text-center p-2"
                >
                  {showMore ? 'Show More' : 'Show Less'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LatestPublications;
